import getHistory from 'globalHistory';
import _ from 'lodash';
import moment from 'moment';
import { format } from '@breezeway/brzw-react-core';

import {
  CHECKIN_CHECKOUT_DATE_FORMAT,
  CHECKIN_CHECKOUT_DATE_TIME_FORMAT,
  CONVERSATIONS_TYPES,
  CONVERSATION_LIST_MODES,
  MESSAGE_TYPES,
  MESSENGER_BASE_PATH,
  MESSENGER_NEW_MESSAGE_ITEM_KEY,
  MESSENGER_NEW_MESSAGE_PATH,
  NUMBER_OF_SHOW_ATTACHMENTS,
  ONE_RESERVATION,
  SHORT_MESSAGE_LENGTH,
} from './constants';

/**
 * @param {String} listMode
 * @return {Boolean}
 */
export const isConversationSelectableMode = listMode =>
  listMode === CONVERSATION_LIST_MODES.SELECTABLE;

/**
 * @param {String} listMode
 * @return {Boolean}
 */
export const isConversationReadableMode = listMode => listMode === CONVERSATION_LIST_MODES.READABLE;

/**
 * @param {Array.<Object>} conversations
 * @return {Boolean}
 */
export const areConversationsEmpty = conversations => _.isEmpty(conversations);

/**
 * @param {Array.<Object>} messages
 * @return {Object}
 */
export const groupMessagesByDay = messages =>
  _.groupBy(messages, message => format.datetime.formatDate(message.createdAt, false, 'LL'));

/**
 * @param {String} conversationType
 * @param {String|Number|null} conversationId
 */
export const updateMessengerRoute = (
  conversationId,
  conversationType = CONVERSATIONS_TYPES.INBOX,
) => {
  const conversationPath = `${MESSENGER_BASE_PATH}/conversations/${conversationType}`;
  let path = conversationPath;

  if (_.isNumber(conversationId)) {
    path = `${conversationPath}/${conversationId}`;
  } else if (conversationId === MESSENGER_NEW_MESSAGE_ITEM_KEY) {
    path = `${conversationPath}/${MESSENGER_NEW_MESSAGE_PATH}`;
  }

  getHistory().push(path);
};

/**
 * @param {String} messageDate
 * @return {String}
 */
export const formatConversationItemDate = messageDate => {
  const todayMoment = moment();
  const messageMoment = moment.utc(messageDate).local();

  if (messageMoment.isBefore(todayMoment, 'day')) {
    return format.datetime.formatDate(messageMoment, false, 'MM/DD/YY');
  }

  return format.datetime.formatTime(messageMoment, 'h:mm A');
};

/**
 * @param {String} message
 * @param {Number} length
 * @return {String}
 */
export const truncateMessage = (message, length = SHORT_MESSAGE_LENGTH) =>
  _.truncate(message, { length });

/**
 * @param {String} display
 * @param {String} searchQuery
 * @return {Boolean}
 */
export const textIncludesQuery = (display, searchQuery) => {
  const parsedQuery = _.toLower(searchQuery);
  const parsedQueryOnlyDigits = _.replace(parsedQuery, /\W/g, ' ');
  const parsedDisplay = _.toLower(display);
  const displayOnlyDigits = _.replace(parsedDisplay, /\W/g, ' ');
  const displayIncludes = _.includes(parsedDisplay, parsedQuery);
  const displayOnlyNumericIncludes = _.includes(displayOnlyDigits, parsedQueryOnlyDigits);

  return displayIncludes || displayOnlyNumericIncludes;
};

/**
 * @param {Object} phoneNumberObject
 * @return {String}
 */
export const getPhoneNumberFromObject = phoneNumberObject => {
  const phoneNumber = _.get(phoneNumberObject, 'phone_number', '');
  const phoneCountry = _.get(phoneNumberObject, 'country_code', 'us');

  return format.phone.formatNumberByCountry(phoneNumber, phoneCountry);
};

/**
 * @param {Array.<Object>} recipients
 * @param {Array} selectedRecipients
 * @return {Array.<Object>}
 */
export const filterRecipients = (recipients, selectedRecipients) =>
  _.filter(recipients, recipient => {
    const recipientID = _.get(recipient, 'id', null);

    return _.includes(selectedRecipients, recipientID);
  });

/**
 * @param {Array.<Object>} messages
 * @return {Array.<Object>}
 */
export const getMessageCollectionForDay = messages =>
  _.chain(messages)
    .groupBy('sender')
    .toPairs()
    .map(item => _.zipObject(['sender', 'messages'], item))
    .value();

/**
 * @param {String} messageA
 * @param {String} messageB
 * @param {Boolean} withSpace
 * @return {String}
 */
const messageWithSpace = (messageA, messageB, withSpace = true) => {
  if (withSpace) {
    return `${messageA} ${messageB}`;
  }

  return `${messageA}${messageB}`;
};

/**
 * @param {Object} message
 * @param {Boolean} withoutSpace
 * @return {String}
 */
export const getMessageTimeElapsed = (message, withoutSpace) => {
  const currentDate = moment.utc();
  const messageDate = moment.utc(message.createdAt);
  const duration = moment.duration(currentDate.diff(messageDate));

  const timeInYears = _.floor(duration.asYears());
  const timeInWeeks = _.floor(duration.asWeeks());
  const timeInDays = _.floor(duration.asDays());
  const timeInHours = _.floor(duration.asHours());
  const timeInMinutes = _.floor(duration.asMinutes());

  if (currentDate < messageDate) {
    return 'now';
  }

  if (timeInYears) {
    return messageWithSpace(timeInYears, 'y', withoutSpace);
  } else if (timeInWeeks) {
    return messageWithSpace(timeInWeeks, 'w', withoutSpace);
  } else if (timeInDays) {
    return messageWithSpace(timeInDays, 'd', withoutSpace);
  } else if (timeInHours) {
    return messageWithSpace(timeInHours, 'h', withoutSpace);
  } else if (timeInMinutes) {
    return messageWithSpace(timeInMinutes, 'm', withoutSpace);
  }

  return 'now';
};

/**
 * @param {Array.<Object>} messages
 * @return {Array.<Object>}
 */
export const mapChatMessage = message => ({
  id: message.id,
  text: message.message,
  date: format.datetime.formatDate(message.created_at, true),
  time: format.datetime.formatDateTime(message.created_at, true, 'h:mm a'),
  createdAt: message.created_at,
  createdDate: format.datetime.formatDate(message.created_at, true, 'YYYY-MM-DD'),
  photos: _.get(message, 'photos', []),
  sender: message.sender,
  user: _.get(message, 'user.full_name'),
  userId: _.get(message, 'user.id'),
  type: _.get(message, 'type_message', 'message'),
});

export const mapChatMessages = messages => _.map(messages, mapChatMessage);

/**
 * @param {Array.<Object>} messages
 * @return {Array.<Object>}
 */
export const prepareMessageStructure = messages => {
  const groupedMessageByDay = groupMessagesByDay(messages);
  const newGroupedMessageByDay = { ...groupedMessageByDay };

  _.forEach(_.keys(groupedMessageByDay), day => {
    newGroupedMessageByDay[day] = getMessageCollectionForDay(groupedMessageByDay[day]);
  });

  return newGroupedMessageByDay;
};

/**
 * @param {String} messageType
 * @return {String}
 */
export const isPreArrivalMessage = messageType => messageType === MESSAGE_TYPES.PRE_ARRIVAL;

/**
 * @param {Object} conversation
 * @return {Object}
 */
export const mapToConversationItemProps = conversation => {
  const fullName = _.get(conversation, 'display', null);
  const phoneNumberObject = _.get(conversation, 'phone_number', null);
  const message = _.get(conversation, 'last_message');
  const phoneNumber = getPhoneNumberFromObject(phoneNumberObject);
  const isPreArrival = isPreArrivalMessage(_.get(message, 'type_message', null));
  const unread = _.get(conversation, 'unread_message_count');
  const createdAtMessage = _.get(message, 'created_at');

  return {
    authorName: fullName || phoneNumber,
    authorPhone: phoneNumber,
    contactId: _.get(conversation, 'contact.id', null),
    conversationId: _.get(conversation, 'id'),
    conversationPreview: _.get(message, 'message'),
    createdAt: createdAtMessage,
    important: _.get(conversation, 'important', false),
    isPreArrival,
    messageDate: format.datetime.formatDate(createdAtMessage, true),
    messageTime: format.datetime.formatDateTime(createdAtMessage, true, 'h:mm a'),
    propertyName: _.get(conversation, 'property_display'),
    unread: unread > 0 ? unread : false,
  };
};

/**
 * @param {Array.<Object>} messages
 * @return {Object}
 */
export const groupMessagesByDate = messages => {
  const unsortedGroupedMessages = {};

  _.forEach(messages, message => {
    const formattedMessage = mapChatMessage(message);
    const dateKey = _.get(formattedMessage, 'createdDate');
    const isNewKey = !_.has(unsortedGroupedMessages, dateKey);

    if (isNewKey) {
      unsortedGroupedMessages[dateKey] = [];
    }

    unsortedGroupedMessages[dateKey].push(formattedMessage);
  });

  const sortedGroupKeys = _.sortBy(_.keys(unsortedGroupedMessages));

  return {
    groupObject: unsortedGroupedMessages,
    sortedKeys: sortedGroupKeys,
  };
};

/**
 * @param {Object} reservation
 * @param {Object} currentDate
 * @return {Bool}
 */
export const filterForCurrentReservation = (reservation, currentDate) => {
  const checkinDate = moment(reservation.checkin_date);
  const checkoutDate = moment(reservation.checkout_date);

  return (
    checkinDate.isSameOrBefore(currentDate, 'day') && checkoutDate.isSameOrAfter(currentDate, 'day')
  );
};

/**
 * @param {Object} reservation
 * @param {Object} currentDate
 * @return {Bool}
 */
export const filterForUpcomingReservation = (reservation, currentDate) => {
  const checkinDate = moment(reservation.checkin_date);

  return checkinDate.isAfter(currentDate, 'day');
};

/**
 * @param {Object} reservation
 * @param {Object} currentDate
 * @return {Bool}
 */
export const filterForRecentReservation = (reservation, currentDate) => {
  const checkoutDate = moment(reservation.checkout_date);

  return checkoutDate.isBefore(currentDate, 'day');
};

/**
 * @param {Array.<Object>} reservations
 * @param {Function} filterFunction
 * @param {Object} currentDate
 * @return {Array}
 */
const getFilteredReservation = (reservations, filterFunction, currentDate) =>
  reservations
    .filter(reservation => filterFunction(reservation, currentDate))
    .head()
    .value();

/**
 * @param {Array.<Object>} reservations
 * @return {Object}
 */
export const getReservationData = reservations => {
  const currentDate = moment();
  const preparedReservationsObject = _.chain(reservations).orderBy('checkin_date', 'asc');
  let reservation = null;
  let title = 'Reservation Details';

  if (_.size(reservations) === ONE_RESERVATION) {
    return {
      reservation: preparedReservationsObject.head().value(),
      title,
    };
  }

  if (!reservation) {
    reservation = getFilteredReservation(
      preparedReservationsObject,
      filterForCurrentReservation,
      currentDate,
    );

    if (!_.isEmpty(reservation)) return { reservation, title };
  }

  if (!reservation) {
    title = 'Next Reservation Details';
    reservation = getFilteredReservation(
      preparedReservationsObject,
      filterForUpcomingReservation,
      currentDate,
    );
    if (!_.isEmpty(reservation)) return { reservation, title };
  }

  if (!reservation) {
    title = 'Past Reservation Details';
    reservation = getFilteredReservation(
      preparedReservationsObject,
      filterForRecentReservation,
      currentDate,
    );
  }

  return { reservation, title };
};

/**
 * @param {Object} message
 * @return {boolean}
 */
export const isAutoMessage = message => {
  const code = _.get(message.type, 'code');

  return _.includes(_.values(MESSAGE_TYPES), code);
};

/**
 * @param {Array.<Object>} conversationMessages
 * @return {Array}
 */
export const getAttachments = conversationMessages =>
  _.chain(conversationMessages)
    .orderBy('id', 'desc')
    .filter(message => !_.isEmpty(message.photos))
    .take(NUMBER_OF_SHOW_ATTACHMENTS)
    .map('photos')
    .flatten()
    .value();

/**
 * @param {Array.<Object>} reservations
 * @param {Array} homes
 * @return {Object}
 */
export const prepareReservation = (reservations, homes) => {
  const reservationData = getReservationData(reservations);
  const reservationToShow = _.get(reservationData, 'reservation', null);
  const reservationTitle = _.get(reservationData, 'title', null);

  if (!_.isEmpty(reservationToShow) && !_.isEmpty(homes[reservationToShow.home_id])) {
    let checkInDate = reservationToShow.checkin_date;
    let checkOutDate = reservationToShow.checkout_date;

    if (!_.isEmpty(reservationToShow.checkin_time)) {
      checkInDate = `${checkInDate}T${reservationToShow.checkin_time}`;
    }
    if (!_.isEmpty(reservationToShow.checkout_time)) {
      checkOutDate = `${checkOutDate}T${reservationToShow.checkout_time}`;
    }

    const home = homes[reservationToShow.home_id];
    const address = _.join(
      _.compact([_.get(home, 'address1'), _.get(home, 'address2'), _.get(home, 'city')]),
      ', ',
    );
    const checkIn = moment(checkInDate);
    const checkOut = moment(checkOutDate);

    return {
      address,
      checkInformation: {
        checkIn: checkIn.format(
          reservationToShow.checkin_time
            ? CHECKIN_CHECKOUT_DATE_TIME_FORMAT
            : CHECKIN_CHECKOUT_DATE_FORMAT,
        ),
        checkOut: checkOut.format(
          reservationToShow.checkout_time
            ? CHECKIN_CHECKOUT_DATE_TIME_FORMAT
            : CHECKIN_CHECKOUT_DATE_FORMAT,
        ),
      },
      home_id: reservationToShow.home_id,
      propertyName: home.display,
      title: reservationTitle,
      type_reservation: _.get(reservationToShow, 'type_reservation.code', null),
    };
  }

  return {};
};
